'use client';

import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { FormikValues } from 'formik';
import { classNames } from 'helpers/classNames';
import { usePbAssets } from 'hooks/usePbAssets';
import { memo, useCallback, useEffect } from 'react';

interface BenchmarkSelectProps {
  form: FormikValues;
}

function BenchmarkSelect({ form }: BenchmarkSelectProps) {
  const { data } = usePbAssets();
  const selected = data?.benchmarks?.find(
    ({ series_id }) => series_id === form.values.benchmark_series_id
  );

  useEffect(() => {
    if (!selected && data?.benchmarks) {
      form.setFieldValue('benchmark_series_id', data?.benchmarks?.[0].series_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.benchmarks]);

  const onChange = useCallback(
    (props: any) => {
      form.setFieldValue('benchmark_series_id', props.series_id);
    },
    [form]
  );

  return (
    <Listbox value={selected || {}} onChange={onChange}>
      {({ open }) => (
        <>
          <Label className='block text-sm font-medium text-gray-700 uppercase'>
            Performance Comparison
          </Label>
          <div className='relative mt-1'>
            <ListboxButton className='h-10 relative w-full cursor-default rounded-md border border-gray-300 border-solid bg-white py-2 pl-3 pr-10 text-left  focus:border-teal-800 focus:outline-none focus:ring-1 focus:ring-teal-800 sm:text-sm'>
              <span className='block truncate'>{selected?.short_name || ''}</span>
              <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
                <ChevronDownIcon className='h-5 w-5 text-gray-400' aria-hidden='true' />
              </span>
            </ListboxButton>

            <Transition
              show={open}
              leave='transition ease-in duration-100'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'>
              <ListboxOptions className='absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm'>
                {data?.benchmarks.map((benchmark) => (
                  <ListboxOption
                    key={benchmark.series_id}
                    className={({ active }) =>
                      classNames(
                        active ? 'text-white bg-teal-800' : 'text-gray-900',
                        'relative cursor-default select-none py-2 pl-3 pr-9'
                      )
                    }
                    value={benchmark}>
                    {({ selected, active }) => (
                      <>
                        <span
                          className={classNames(
                            selected ? 'font-semibold' : 'font-normal',
                            'block truncate'
                          )}>
                          {benchmark.short_name}
                        </span>

                        {selected ? (
                          <span
                            className={classNames(
                              active ? 'text-white' : 'text-teal-800',
                              'absolute inset-y-0 right-0 flex items-center pr-4'
                            )}>
                            <CheckIcon className='h-5 w-5' aria-hidden='true' />
                          </span>
                        ) : null}
                      </>
                    )}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Transition>
          </div>
        </>
      )}
    </Listbox>
  );
}

BenchmarkSelect.displayName = 'BenchmarkSelect';

export default memo(BenchmarkSelect);
